import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, 
  X, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { uploadAPI } from '../utils/api';
import type { UploadPreview, ColumnMapping } from '../types';

interface CSVUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type UploadStep = 'drop' | 'preview' | 'mapping' | 'processing' | 'success' | 'error';

export default function CSVUploadModal({ isOpen, onClose, onSuccess }: CSVUploadModalProps) {
  const [step, setStep] = useState<UploadStep>('drop');
  const [uploadPreview, setUploadPreview] = useState<UploadPreview | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState<any>(null);

  // Reset modal state when opening
  React.useEffect(() => {
    if (isOpen) {
      setStep('drop');
      setUploadPreview(null);
      setSelectedFile(null);
      setColumnMapping({});
      setError('');
      setUploadResult(null);
    }
  }, [isOpen]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file');
      setStep('error');
      return;
    }

    setSelectedFile(file);
    setStep('preview');

    try {
      const preview = await uploadAPI.previewCSV(file);
      setUploadPreview(preview);
      setColumnMapping(preview.suggested_mapping);
      setStep('mapping');
    } catch (err: any) {
      console.error('Upload preview error:', err);
      setError(err.response?.data?.detail || 'Failed to preview CSV file');
      setStep('error');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/csv': ['.csv']
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB limit
  });

  const handleProcessUpload = async () => {
    if (!selectedFile || !uploadPreview) return;

    setProcessing(true);
    setStep('processing');

    try {
      const result = await uploadAPI.processCSV(selectedFile, columnMapping);
      setUploadResult(result);
      setStep('success');
      // Auto close and refresh after success
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Process upload error:', err);
      setError(err.response?.data?.detail || 'Failed to process CSV file');
      setStep('error');
    } finally {
      setProcessing(false);
    }
  };

  const requiredFields = ['LoadID', 'Dispatcher', 'PickupDate', 'DeliveryDate', 'LoadedMiles', 'CarrierRate'];
  const mappedFields = Object.keys(columnMapping);
  const missingRequired = requiredFields.filter(field => !mappedFields.includes(field));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto netflix-card bg-netflix-grey-800">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-netflix-grey-600">
          <div className="flex items-center space-x-3">
            <div className="bg-netflix-red p-2 rounded-lg">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Upload CSV File</h2>
              <p className="text-sm text-netflix-grey-400">Add loads to your dashboard</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-netflix-grey-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: File Drop */}
          {step === 'drop' && (
            <div
              {...getRootProps()}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer ${
                isDragActive
                  ? 'border-netflix-red bg-netflix-red/10'
                  : 'border-netflix-grey-600 bg-netflix-grey-800/50 hover:border-netflix-red hover:bg-netflix-red/5'
              }`}
            >
              <input {...getInputProps()} />
              <div className="space-y-4">
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
                  isDragActive ? 'bg-netflix-red' : 'bg-netflix-grey-600'
                }`}>
                  <Upload className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-white mb-2">
                    {isDragActive ? 'Drop your CSV file here' : 'Drag & drop your CSV file'}
                  </p>
                  <p className="text-netflix-grey-400 mb-4">
                    or click to browse files
                  </p>
                  <div className="inline-block bg-netflix-red/20 text-netflix-red px-3 py-1 rounded-full text-sm">
                    Maximum file size: 50MB
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: File Preview */}
          {step === 'preview' && (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-netflix-red border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-white text-lg">Analyzing your CSV file...</p>
              <p className="text-netflix-grey-400">This will just take a moment</p>
            </div>
          )}

          {/* Step 3: Column Mapping */}
          {step === 'mapping' && uploadPreview && (
            <div className="space-y-6">
              <div className="bg-netflix-grey-700 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <FileText className="w-5 h-5 text-yuli-green" />
                  <div>
                    <h3 className="font-semibold text-white">{uploadPreview.filename}</h3>
                    <p className="text-sm text-netflix-grey-400">
                      {uploadPreview.row_count.toLocaleString()} rows found
                    </p>
                  </div>
                </div>
                
                {/* Sample data preview */}
                <div className="mt-4 overflow-x-auto">
                  <div className="bg-netflix-black/30 rounded p-3 text-xs">
                    <p className="text-netflix-grey-400 mb-2">Sample data (first 2 rows):</p>
                    <div className="space-y-1">
                      {uploadPreview.sample_data.slice(0, 2).map((row, idx) => (
                        <div key={idx} className="text-netflix-grey-300 font-mono">
                          {Object.entries(row).slice(0, 5).map(([key, value]) => (
                            <span key={key} className="mr-4">
                              <span className="text-yuli-blue">{key}:</span> {String(value).substring(0, 20)}
                            </span>
                          ))}
                          ...
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Map Your Columns</h4>
                <p className="text-netflix-grey-400 mb-4">
                  Connect your CSV columns to the required fields. We've made some smart suggestions!
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'LoadID', label: 'Load Number', required: true },
                    { key: 'Dispatcher', label: 'Dispatcher', required: true },
                    { key: 'Driver', label: 'Driver', required: false },
                    { key: 'Customer', label: 'Customer', required: false },
                    { key: 'Equipment', label: 'Equipment', required: false },
                    { key: 'PickupDate', label: 'Pickup Date', required: true },
                    { key: 'DeliveryDate', label: 'Delivery Date', required: true },
                    { key: 'LoadedMiles', label: 'Loaded Miles', required: true },
                    { key: 'EmptyMiles', label: 'Empty Miles', required: false },
                    { key: 'CustomerMiles', label: 'Customer Miles', required: false },
                    { key: 'CustomerFreightCharge', label: 'Customer Freight Charge', required: false },
                    { key: 'CarrierRate', label: 'Carrier Rate', required: false },
                  ].map(field => (
                    <div key={field.key} className="space-y-2">
                      <label className="block text-sm font-medium text-white">
                        {field.label}
                        {field.required && <span className="text-netflix-red ml-1">*</span>}
                      </label>
                      <select
                        value={columnMapping[field.key] || ''}
                        onChange={(e) => setColumnMapping(prev => ({
                          ...prev,
                          [field.key]: e.target.value
                        }))}
                        className="w-full bg-netflix-grey-700 border border-netflix-grey-600 text-white rounded px-3 py-2 focus:border-netflix-red outline-none"
                      >
                        <option value="">Select column...</option>
                        {uploadPreview.columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {missingRequired.length > 0 && (
                  <div className="mt-4 bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <h5 className="font-semibold text-red-300">Missing Required Fields</h5>
                    </div>
                    <p className="text-red-200 text-sm">
                      Please map these required fields: {missingRequired.join(', ')}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('drop')}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <span>Choose Different File</span>
                </button>
                <button
                  onClick={handleProcessUpload}
                  disabled={missingRequired.length > 0}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Process Upload</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Processing */}
          {step === 'processing' && (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 text-netflix-red animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Processing Your Data</h3>
              <p className="text-netflix-grey-400">
                We're importing your loads and calculating RPM metrics...
              </p>
              <div className="mt-6 bg-netflix-grey-700 rounded-full h-2 max-w-xs mx-auto">
                <div className="bg-netflix-red h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 'success' && uploadResult && (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-yuli-green mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Upload Successful!</h3>
              <div className="space-y-2 text-netflix-grey-300">
                <p>✅ {uploadResult.processed_count || 0} loads uploaded successfully</p>
                {uploadResult.duplicate_count > 0 && (
                  <p>📋 {uploadResult.duplicate_count} duplicates skipped</p>
                )}
                {uploadResult.error_count > 0 && (
                  <p>⚠️ {uploadResult.error_count} rows had errors</p>
                )}
                {uploadResult.message && (
                  <p className="text-yuli-blue font-medium">{uploadResult.message}</p>
                )}
                <p className="text-yuli-green font-semibold">Your dashboard is updating now!</p>
              </div>
            </div>
          )}

          {/* Step 6: Error */}
          {step === 'error' && (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Upload Failed</h3>
              <p className="text-red-300 mb-6">{error}</p>
              <button
                onClick={() => setStep('drop')}
                className="btn-primary"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}